import { useCallback, useEffect, useState } from "react";
import { notify } from "../../../lib/globalNotify";
import { sendToTerminal } from "../../../lib/terminalLogger";
import { readWithAliases, Keys } from "../../../lib/storage/keys";
import { corelCommunication } from "../../../lib/corel/communication";
import { runProductionBatchExport } from "../../ExportModal/services/runProductionBatchExport";
import { parseOrderFromFilename } from "../../../utils/parseOrderFromFilename";
import {
	getExportProducaoOfflineConfig,
	type OfflineServiceId,
} from "../../../utils/exportProducaoOfflineConfig";
import { inferOfflineService } from "../../../utils/inferOfflineService";
import type { ExportProducaoOfflineOptions } from "../components/ExportProducaoOfflineModal";

interface DocumentInfoResult {
	nomeArquivo?: string;
	caminhoArquivo?: string;
}

function isCorelOk(resp: unknown): boolean {
	if (!resp || typeof resp !== "object") return false;
	const status = String((resp as { status?: string }).status ?? "").toLowerCase();
	return status === "ok" || status === "success";
}

async function fetchDocumentInfo(): Promise<DocumentInfoResult | null> {
	return new Promise((resolve) => {
		try {
			corelCommunication.sendActionGuarded(
				"obter-info-documento",
				{},
				(resp: unknown) => {
					if (!isCorelOk(resp)) {
						resolve(null);
						return;
					}
					const result = (resp as { result?: DocumentInfoResult }).result;
					resolve(result ?? null);
				},
			);
		} catch {
			resolve(null);
		}
	});
}

export interface OfflineDocumentContext {
	documentInfo: DocumentInfoResult | null;
	documentLoading: boolean;
	parsedOrcId: number | null;
	parsedOitId: number | null;
	parsedMaterial: string | null;
	inferredServiceId: OfflineServiceId | null;
}

const EMPTY_DOC_CONTEXT: OfflineDocumentContext = {
	documentInfo: null,
	documentLoading: false,
	parsedOrcId: null,
	parsedOitId: null,
	parsedMaterial: null,
	inferredServiceId: null,
};

export function useExportProducaoOffline(isModalOpen = false) {
	const [isExporting, setIsExporting] = useState(false);
	const [docContext, setDocContext] =
		useState<OfflineDocumentContext>(EMPTY_DOC_CONTEXT);

	const loadDocumentContext = useCallback(async () => {
		setDocContext((prev) => ({ ...prev, documentLoading: true }));
		const info = await fetchDocumentInfo();
		const fileName = info?.nomeArquivo ?? "";
		const parsed = parseOrderFromFilename(fileName);
		const config = getExportProducaoOfflineConfig();
		const inferred = parsed?.material
			? inferOfflineService(parsed.material, config)
			: null;

		setDocContext({
			documentInfo: info,
			documentLoading: false,
			parsedOrcId: parsed?.orcId ?? null,
			parsedOitId: parsed?.oitId ?? null,
			parsedMaterial: parsed?.material ?? null,
			inferredServiceId: inferred,
		});
	}, []);

	useEffect(() => {
		if (isModalOpen) {
			void loadDocumentContext();
		} else {
			setDocContext(EMPTY_DOC_CONTEXT);
		}
	}, [isModalOpen, loadDocumentContext]);

	const runExport = useCallback(
		async (options: ExportProducaoOfflineOptions) => {
			setIsExporting(true);

			try {
				const savedData =
					readWithAliases<Record<string, unknown>>(Keys.export.currentSaveData, [
						"currentSaveData",
					], { area: "session" }) ?? {};

				const orcId =
					options.orcId ??
					docContext.parsedOrcId ??
					Number(
						(savedData?.pedido as { orc_id?: number } | undefined)?.orc_id ?? 0,
					);
				const oitId =
					options.oitId ??
					docContext.parsedOitId ??
					Number(
						(savedData?.pedido as { oit_id?: number } | undefined)?.oit_id ?? 0,
					);

				const enrichedSaved = { ...savedData };
				if (orcId > 0 && oitId > 0 && !enrichedSaved.pedido) {
					enrichedSaved.pedido = { orc_id: orcId, oit_id: oitId };
				}

				notify({
					type: "info",
					title: "Exportação offline",
					message: "Exportando para pasta de contingência...",
					duration: 3000,
				});

				sendToTerminal("📤 [EXPORT_OFFLINE] Iniciando rescue batch", {
					outputDir: options.outputDir,
					format: options.format,
					root: options.root,
					serviceId: options.serviceId,
					orcId,
					oitId,
				});

				const result = await runProductionBatchExport({
					savedData: enrichedSaved,
					format: options.format,
					outputDir: options.outputDir,
					mode: "rescue",
					orcId,
					oitId,
					includeLayoutPng: options.includeLayoutPng,
					registerBatch: orcId > 0 && oitId > 0,
				});

				if (result.registered) {
					notify({
						type: "success",
						title: "Exportação concluída",
						message: "Arquivos exportados e registrados no pedido.",
						duration: 5000,
					});
				} else if (orcId > 0 && oitId > 0) {
					// toast de falha de registro já exibido pelo motor
				} else {
					notify({
						type: "success",
						title: "Exportação concluída",
						message: `Arquivos exportados em:\n${options.outputDir}\n(Sem pedido em contexto — registro não realizado.)`,
						duration: 6000,
					});
				}

				return { success: true, outputDir: options.outputDir, ...result };
			} catch (err) {
				const message = err instanceof Error ? err.message : String(err);
				sendToTerminal("❌ [EXPORT_OFFLINE] Falha", { error: message });
				notify({
					type: "error",
					title: "Falha na exportação",
					message,
					duration: 8000,
				});
				return { success: false, error: message };
			} finally {
				setIsExporting(false);
			}
		},
		[docContext.parsedOrcId, docContext.parsedOitId],
	);

	return { isExporting, runExport, docContext };
}
